// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The interactive tutorial: six steps, on the real board, driving the SHIPPED rules.
//
// ── WHY IT IS DATA AND NOT A SECOND GAME ────────────────────────────────────────────────────────
// Every step's `perform` writes into the same order set a thumb writes into and then calls the same
// `commitTurn` the Go button calls, against the same match the player is looking at. There is no
// mock board anywhere in this file, and that is deliberate: a tutorial with its own private
// implementation is a lie the moment anybody touches the rules, and it is not even a DETECTABLE lie
// — it would go on teaching the old game, confidently, for ever.
//
// Because it is data, `tests/tutorial.test.ts` replays all six steps through the real rules and
// asserts each one is legal and reachable from the state the previous step produced. Change a rule
// and the tutorial goes RED instead of going stale.
//
// ── THE SHAPE OF THE SEQUENCE ───────────────────────────────────────────────────────────────────
// Step 1 is one drag that obviously works. Steps 2 and 3 add the two halves of an order — where you
// go and where you look — because neither means anything without the other. Step 4 is the first time
// the game argues back: the route you drew walks through ground somebody is already watching, and
// the overlay was telling you so. Step 5 is the mast, which is what winning actually is. Step 6 is
// the moment it becomes interesting: you deliberately arrive LATE, after their watch has swept past,
// which is a play you can only make because the route is drawn on a timeline instead of a board.

import { MOVE, STALK, holdAll, type SideOrders, type Stance } from './orders';
import { commitTurn, createMatch, mastsFor, sectorOf, type MatchState } from './rules';
import { distanceField, idx, tx, ty } from './map';

/** Which interaction is live during a step. Everything else is refused. */
export type Allow = 'path' | 'watch' | 'stance' | 'go';

export interface TutorialStep {
  id: string;
  /** One sentence, imperative, naming the thing to touch. */
  copy: string;
  /** What the player learns once they have done it. */
  after: string;
  allow: Allow;
  /** Other interactions this step also permits, when its move takes more than one gesture. */
  also?: readonly Allow[];
  /** The move this step asks for, made through the shipped order set. */
  perform(s: MatchState, orders: SideOrders): boolean;
}

export const TUTORIAL_SEED = 4242;

/** A tile this unit can reach that is nearer the mast — where step 1 is pointing. */
function towardMast(s: MatchState, n: number): number {
  const u = s.units.filter((x) => x.side === 0)[n];
  const mast = mastsFor(s.mode, s.turn)[0];
  const from = idx(Math.floor(u.x), Math.floor(u.y));
  const dist = distanceField(s.map, mast);
  const reach = distanceField(s.map, from);
  let best = -1;
  let bestD = dist[from];
  for (let t = 0; t < 192; t++) {
    if (reach[t] < 0 || reach[t] > 8 || dist[t] < 0) continue;
    if (dist[t] < bestD) {
      bestD = dist[t];
      best = t;
    }
  }
  return best;
}

export const STEPS: readonly TutorialStep[] = [
  {
    id: 'path',
    copy: 'Tap your Scout, then drag on the board to draw where it walks.',
    after: 'The dots on that route are seconds. That is WHEN it arrives, not just where.',
    allow: 'path',
    perform: (s, orders) => {
      const t = towardMast(s, 0);
      if (t < 0) return false;
      orders[0] = { ...orders[0], path: [t] };
      return true;
    },
  },
  {
    id: 'watch',
    copy: 'Now drag the arrowhead to choose which way it watches when it gets there.',
    after: 'A unit only shoots inside the wedge it is watching. Everything else walks straight past.',
    allow: 'watch',
    perform: (s, orders) => {
      const u = s.units[0];
      const mast = mastsFor(s.mode, s.turn)[0];
      orders[0] = { ...orders[0], watch: sectorOf(tx(mast) + 1 - u.x, ty(mast) + 1 - u.y) };
      return true;
    },
  },
  {
    id: 'stance',
    copy: 'Set it to Stalk — half the speed, and none of the cost to your aim.',
    after: 'Moving fast is the slow way to arrive. Rushing cannot shoot at all.',
    allow: 'stance',
    perform: (_s, orders) => {
      orders[0] = { ...orders[0], stance: STALK as Stance };
      return true;
    },
  },
  {
    id: 'go',
    copy: 'Run the five seconds and watch both plans happen at once.',
    after: 'Neither of you saw the other’s orders. That is the whole game.',
    allow: 'go',
    also: ['path', 'watch', 'stance'],
    perform: (s, orders) => {
      const foe = holdAll(s.mode.units);
      commitTurn(s, orders, foe, false);
      return true;
    },
  },
  {
    id: 'mast',
    copy: 'Send everyone to the marked square — that is the mast.',
    after: 'You bank time only while your side is in the field and theirs is not. Bank enough and you win.',
    allow: 'path',
    also: ['watch', 'stance'],
    perform: (s, orders) => {
      const mast = mastsFor(s.mode, s.turn)[0];
      let any = false;
      for (let n = 0; n < s.mode.units; n++) {
        const u = s.units.filter((x) => x.side === 0)[n];
        if (!u.alive) continue;
        const from = idx(Math.floor(u.x), Math.floor(u.y));
        const reach = distanceField(s.map, from);
        const target = [mast, mast + 1, mast + 12, mast + 13].find((t) => reach[t] >= 0 && reach[t] <= 10);
        if (target === undefined) continue;
        orders[n] = { ...orders[n], path: [target], stance: MOVE as Stance };
        any = true;
      }
      return any;
    },
  },
  {
    id: 'late',
    copy: 'Now run it. Watch where their wedges point — and arrive after they have swept past.',
    after:
      'That is the highest play in the game: you cannot outshoot somebody who is already looking at you, so you arrange not to be there yet.',
    allow: 'go',
    also: ['path', 'watch', 'stance'],
    perform: (s, orders) => {
      const foe = holdAll(s.mode.units);
      commitTurn(s, orders, foe, false);
      return true;
    },
  },
];

export function createTutorialMatch(): MatchState {
  return createMatch(TUTORIAL_SEED, 'crossfire');
}

export interface TutorialRun {
  index: number;
  step(): TutorialStep | null;
  allows(kind: Allow): boolean;
  did(kind: Allow): void;
  done(): boolean;
  skip(): void;
  lastAfter: string;
}

export function createRun(): TutorialRun {
  const run: TutorialRun = {
    index: 0,
    lastAfter: '',
    step: () => STEPS[run.index] ?? null,
    allows: (kind) => {
      const s = run.step();
      if (!s) return true;
      return s.allow === kind || (s.also?.includes(kind) ?? false);
    },
    did: (kind) => {
      const s = run.step();
      if (!s || s.allow !== kind) return;
      run.lastAfter = s.after;
      run.index++;
    },
    done: () => run.index >= STEPS.length,
    skip: () => {
      run.index = STEPS.length;
    },
  };
  return run;
}
